import {memo, ReactNode} from 'react';

import {useSelector} from 'react-redux';
import styled, {css} from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {selectA11yContrast} from '~/shared/store/selectors';
import mobileBackWhiteUrl from '~/assets/images/icons/mobile_back_white.svg';
import mobileBackBlueUrl from '~/assets/images/icons/mobile_back_blue.svg';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
 
import {flexCenter} from '../theme/FlexLayout';
import {flipOnLTR, rotateOnRTL} from '../theme/flipOnLTR';

import Button from './Button';
import {HeaderWrapper, CloseButtonSVGIcon, Title} from './styled';

const CloseButton = styled(Button)`
  width: 32px;
  align-self: flex-start;
  cursor: pointer;
  height: 32px;
  border-radius: 50%;
  ${flexCenter}
  background-color: ${({withBackground, theme}) => withBackground && theme.colors.darkBackground};
  position: absolute;
  top: ${({isDual}) => (isDual ? 22 : 13)}px;
  ${flipOnLTR`left: 18px;`}
  ${rotateOnRTL}
  ${({flipDirection}) => flipDirection && css`
    ${flipOnLTR` 
      left: auto;
      right: 18px;
    `}
  `}
  z-index: 3;
`;

const BackButtonImage = styled(ImageWithAlt).attrs(({isContrastActive}: {isContrastActive: boolean}) => ({
  src: isContrastActive ? mobileBackWhiteUrl : mobileBackBlueUrl,
}))<{alt: string; isContrastActive: boolean}>`
  height: 17px;
  width: 17px;
  cursor: pointer;
  display: flex;
`;

type LayoutWithHeaderProps = {
  titleKey: string;
  onClick?: (() => void) | null;
  showBackArrow?: boolean;
  titleComponent?: any;
  showHeader?: boolean;
  showCloseButton?: boolean;
  children?: ReactNode;
};

export const LayoutWithHeader = memo(
  ({
    titleKey,
    onClick,
    showBackArrow,
    titleComponent,
    showHeader = true,
    showCloseButton = true,
    children,
  }: Readonly<LayoutWithHeaderProps>) => {
    const isContrastActive = useSelector(selectA11yContrast);

    const {t} = getLocalizationService();

    const HeaderTitle = titleComponent || Title;

    if (!showHeader) {
      return <>{children}</>;
    }

    return (
      <>
        <HeaderWrapper>
          <HeaderTitle id="modal-title" data-id={titleKey}>
            {t(titleKey)}
          </HeaderTitle>
        </HeaderWrapper>

        {children}

        {showCloseButton && (
          <CloseButton onClick={onClick} aria-label={t(showBackArrow ? 'back' : 'close')} flipDirection={showBackArrow}>
            {showBackArrow ? (
              <BackButtonImage alt={t('back')} isContrastActive={isContrastActive} />
            ) : (
              <CloseButtonSVGIcon />
            )}
          </CloseButton>
        )}
      </>
    );
  },
);
